import { useTranslation } from "react-i18next";
import {
  Bot,
  Mountain,
  Play,
  Compass,
  Zap,
  Globe,
  ArrowRight,
  CheckCircle2,
  Workflow,
  BookOpen,
  FileSearch,
  ShieldCheck,
  BarChart3,
  ClipboardCheck,
  Plug,
  Library,
  Wand2,
  ListChecks,
} from "lucide-react";
import LocaleLink from "@/components/LocaleLink";
import { Fibo } from "@/components/brand/Fibo";
import { usePlatformMetrics } from "@/hooks/usePlatformMetrics";

interface MegaMenuPlatformProps {
  onItemClick?: () => void;
}

const MegaMenuPlatform = ({ onItemClick }: MegaMenuPlatformProps) => {
  const { t } = useTranslation();
  const metrics = usePlatformMetrics();

  return (
    <div className="w-[1180px] p-6">
      <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1.1fr] gap-6">
        {/* AI & Automation */}
        <div>
          <h3 className="eyebrow mb-4">
            {t("megaMenu.ai")}
          </h3>
          <div className="space-y-1">
            <LocaleLink
              to="/features"
              onClick={onItemClick}
              className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
            >
              <Bot className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t("megaMenu.aiAgents")}</div>
                <div className="text-xs text-muted-foreground">{t("megaMenu.aiAgentsDesc")}</div>
              </div>
            </LocaleLink>

            <LocaleLink
              to="/features#assistant"
              onClick={onItemClick}
              className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
            >
              <Fibo pose="think" size={25} decorative className="shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t("megaMenu.csquareAssistant")}</div>
                <div className="text-xs text-muted-foreground">{t("megaMenu.csquareAssistantDesc")}</div>
              </div>
            </LocaleLink>

            <LocaleLink
              to="/integrations/continuous-monitoring"
              onClick={onItemClick}
              className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
            >
              <Zap className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t("megaMenu.continuousMonitoring")}</div>
                <div className="text-xs text-muted-foreground">{t("megaMenu.continuousMonitoringDesc")}</div>
              </div>
            </LocaleLink>

            <LocaleLink
              to="/features#workflows"
              onClick={onItemClick}
              className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
            >
              <Workflow className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t("megaMenu.dagWorkflows")}</div>
                <div className="text-xs text-muted-foreground">{t("megaMenu.dagWorkflowsDesc")}</div>
              </div>
            </LocaleLink>
          </div>
        </div>

        {/* Intelligence */}
        <div>
          <h3 className="eyebrow mb-4">
            {t("megaMenu.intelligenceRisk")}
          </h3>
          <div className="space-y-1">
            <LocaleLink
              to="/features#regulatory-intelligence"
              onClick={onItemClick}
              className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
            >
              <BookOpen className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t("megaMenu.regulatoryIntelligence")}</div>
                <div className="text-xs text-muted-foreground">{t("megaMenu.regulatoryIntelligenceDesc")}</div>
              </div>
            </LocaleLink>

            <LocaleLink
              to="/features#document-intelligence"
              onClick={onItemClick}
              className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
            >
              <FileSearch className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t("megaMenu.documentIntelligence")}</div>
                <div className="text-xs text-muted-foreground">{t("megaMenu.documentIntelligenceDesc")}</div>
              </div>
            </LocaleLink>

            <LocaleLink
              to="/advanced-analytics"
              onClick={onItemClick}
              className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
            >
              <BarChart3 className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t("megaMenu.advancedAnalytics")}</div>
                <div className="text-xs text-muted-foreground">{t("megaMenu.advancedAnalyticsDesc")}</div>
              </div>
            </LocaleLink>

            <LocaleLink
              to="/enterprise-risk"
              onClick={onItemClick}
              className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
            >
              <ShieldCheck className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t("megaMenu.enterpriseRisk")}</div>
                <div className="text-xs text-muted-foreground">{t("megaMenu.enterpriseRiskDesc")}</div>
              </div>
            </LocaleLink>
          </div>
        </div>

        {/* Compliance Operations */}
        <div>
          <h3 className="eyebrow mb-4">
            {t("megaMenu.complianceOps")}
          </h3>
          <div className="space-y-1">
            <LocaleLink
              to="/automated-control-mapping"
              onClick={onItemClick}
              className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
            >
              <ClipboardCheck className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t("megaMenu.automatedControlMapping")}</div>
                <div className="text-xs text-muted-foreground">{t("megaMenu.automatedControlMappingDesc")}</div>
              </div>
            </LocaleLink>

            <LocaleLink
              to="/control-applicability-wizard"
              onClick={onItemClick}
              className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
            >
              <Wand2 className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t("megaMenu.controlApplicabilityWizard")}</div>
                <div className="text-xs text-muted-foreground">{t("megaMenu.controlApplicabilityWizardDesc")}</div>
              </div>
            </LocaleLink>

            <LocaleLink
              to="/enterprise-audit"
              onClick={onItemClick}
              className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
            >
              <FileSearch className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t("megaMenu.enterpriseAudit")}</div>
                <div className="text-xs text-muted-foreground">{t("megaMenu.enterpriseAuditDesc")}</div>
              </div>
            </LocaleLink>

            <LocaleLink
              to="/trust"
              onClick={onItemClick}
              className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
            >
              <ShieldCheck className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t("megaMenu.trustCenter")}</div>
                <div className="text-xs text-muted-foreground">{t("megaMenu.trustCenterDesc")}</div>
              </div>
            </LocaleLink>
          </div>
        </div>

        {/* Coverage & Ecosystem */}
        <div>
          <h3 className="eyebrow mb-4">
            {t("megaMenu.coverageEcosystem")}
          </h3>
          <div className="space-y-1">
            <LocaleLink
              to="/framework-library"
              onClick={onItemClick}
              className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
            >
              <Library className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t("megaMenu.frameworkLibrary")}</div>
                <div className="text-xs text-muted-foreground">{t("megaMenu.frameworkLibraryDesc")}</div>
              </div>
            </LocaleLink>

            <LocaleLink
              to="/integrations"
              onClick={onItemClick}
              className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
            >
              <Plug className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t("megaMenu.integrations")}</div>
                <div className="text-xs text-muted-foreground">{t("megaMenu.integrationsDesc")}</div>
              </div>
            </LocaleLink>

            <LocaleLink
              to="/see-what-applies"
              onClick={onItemClick}
              className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
            >
              <ListChecks className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t("megaMenu.seeWhatApplies")}</div>
                <div className="text-xs text-muted-foreground">{t("megaMenu.seeWhatAppliesDesc")}</div>
              </div>
            </LocaleLink>

            <LocaleLink
              to="/global-coverage"
              onClick={onItemClick}
              className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
            >
              <Globe className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t("megaMenu.globalCoverage")}</div>
                <div className="text-xs text-muted-foreground">
                  {t("megaMenu.globalCoverageDesc", {
                    mapped: metrics.jurisdictions_with_mapped_frameworks,
                    frameworks: metrics.frameworks_in_picker,
                  })}
                </div>
              </div>
            </LocaleLink>

            <LocaleLink
              to="/platform-tour"
              onClick={onItemClick}
              className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
            >
              <Play className="h-5 w-5 text-accent mt-0.5" />
              <div>
                <div className="font-medium text-sm group-hover:text-primary">{t("megaMenu.platformTour")}</div>
                <div className="text-xs text-muted-foreground">{t("megaMenu.platformTourDesc")}</div>
              </div>
            </LocaleLink>
          </div>
        </div>

        {/* Latest Release Right Rail */}
        <div className="flex flex-col bg-brand-ink p-5 text-brand-ink-foreground">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold tracking-wider bg-trail-teal text-brand-ink">
              {t("megaMenu.latestPill")}
            </span>
            <span className="font-mono text-xs font-semibold text-brand-ink-foreground/60">
              {t("megaMenu.releaseVersion")}
            </span>
          </div>

          <LocaleLink
            to="/whats-new?v=v1.3"
            onClick={onItemClick}
            className="block group"
          >
            <h4 className="text-sm font-semibold leading-snug text-brand-ink-foreground transition-colors group-hover:text-trail-teal">
              {t("megaMenu.releaseTitle")}
            </h4>
            <div className="mb-3 mt-0.5 text-[11px] text-brand-ink-foreground/55">
              {t("megaMenu.releaseDate")}
            </div>
          </LocaleLink>

          <ul className="space-y-1.5 mb-4">
            <li className="flex items-start gap-2 text-xs text-brand-ink-foreground/75">
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-trail-teal" />
              <span>{t("megaMenu.releaseBullet1")}</span>
            </li>
            <li className="flex items-start gap-2 text-xs text-brand-ink-foreground/75">
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-trail-teal" />
              <span>{t("megaMenu.releaseBullet2")}</span>
            </li>
            <li className="flex items-start gap-2 text-xs text-brand-ink-foreground/75">
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-trail-teal" />
              <span>{t("megaMenu.releaseBullet3")}</span>
            </li>
          </ul>

          <div className="mt-auto space-y-2 border-t border-brand-ink-foreground/15 pt-3">
            <LocaleLink
              to="/whats-new?v=v1.3"
              onClick={onItemClick}
              className="flex items-center gap-1.5 text-xs font-semibold text-trail-teal hover:underline"
            >
              {t("megaMenu.readReleaseNotes")}
              <ArrowRight className="h-3 w-3" />
            </LocaleLink>
            <LocaleLink
              to="/whats-new"
              onClick={onItemClick}
              className="block text-xs text-brand-ink-foreground/55 transition-colors hover:text-brand-ink-foreground"
            >
              {t("megaMenu.allReleases")}
            </LocaleLink>
          </div>
        </div>
      </div>

      {/* Positioning footer row */}
      <div className="mt-6 pt-4 border-t border-border flex flex-wrap items-center gap-x-6 gap-y-2">
        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
          {t("megaMenu.learnMore")}
        </span>
        <LocaleLink
          to="/ai-grc-platform"
          onClick={onItemClick}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground hover:text-primary transition-colors"
        >
          <Fibo pose="wave" size={32} decorative />
          {t("megaMenu.aiGrcPlatform")}
        </LocaleLink>
        <LocaleLink
          to="/our-approach"
          onClick={onItemClick}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground hover:text-primary transition-colors"
        >
          <Mountain className="h-3.5 w-3.5" />
          {t("megaMenu.ourApproach")}
        </LocaleLink>
        <LocaleLink
          to="/why-csquare"
          onClick={onItemClick}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground hover:text-primary transition-colors"
        >
          <Compass className="h-3.5 w-3.5" />
          {t("megaMenu.whyCsquare")}
        </LocaleLink>
      </div>
    </div>
  );
};

export default MegaMenuPlatform;
